import {
  AfterViewInit,
  Component,
  computed,
  inject,
  NgZone,
  OnDestroy,
  OnInit,
  ElementRef,
  signal,
  ViewChild,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ActivatedRouteSnapshot, NavigationEnd, Router } from "@angular/router";
import { filter } from "rxjs";
import {
  DeviceDetection,
  DeviceDetector,
} from "@bitwarden/web-vault/app/shared/utils/device-detector";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import { AppStateStoreService } from "@bitwarden/web-vault/app/services/store/app-state/app-state.store.service";
import { PluginConfigService } from "@bitwarden/web-vault/app/services/store/data-plugin/plugin-config.service";
import { DialogFactory } from "@bitwarden/web-vault/app/components/dialog/dialogFactory";
import { ProviderService } from "@bitwarden/common/abstractions/provider.service";
import { SyncService } from "@bitwarden/common/abstractions/sync/sync.service.abstraction";
import { Provider } from "@bitwarden/common/models/domain/provider";

import { Origin } from "@bitwarden/web-vault/app/models/types/general-types";
import { PaymentService } from "@bitwarden/web-vault/app/services/subscription/payment.service";
import {
  GuideWizardService,
  tasks,
} from "@vault/web-vault/app/services/guideWizard/guide-wizard.service";
import { CreditsService } from "@bitwarden/web-vault/app/services/syncing/gloss-credits/credits.service";
import { ScrollDirectionService } from "@bitwarden/web-vault/app/services/scroll-direction.service";
import { playPageEnterAnimation } from "@bitwarden/web-vault/app/shared/utils/page-transition.util";

/**
 * Whether the active route - or any route above it - opts out of <main>'s phone side gutter with
 * `data: { fullBleed: true }`, for a page meant to fill the screen edge to edge (#6171).
 */
export function routeIsFullBleed(route: ActivatedRouteSnapshot | null): boolean {
  for (let current = route; current; current = current.firstChild) {
    if (current.data?.["fullBleed"] === true) {
      return true;
    }
  }
  return false;
}

@Component({
  selector: "app-user-layout",
  templateUrl: "user-layout.component.html",
})
export class UserLayoutComponent implements OnInit, AfterViewInit, OnDestroy {
  protected userStore = inject(UserStoreService);
  protected appState = inject(AppStateStoreService);
  protected pluginConfigService = inject(PluginConfigService);
  protected dialogFactory: DialogFactory;
  private providerService: ProviderService = inject(ProviderService);
  private syncService: SyncService = inject(SyncService);
  private guideWizardService: GuideWizardService = inject(GuideWizardService);
  private creditsService: CreditsService = inject(CreditsService);
  private paymentService: PaymentService = inject(PaymentService);
  private scrollDirectionService: ScrollDirectionService = inject(ScrollDirectionService);
  private ngZone = inject(NgZone);

  private router = inject(Router);

  private isWizardOpened = false;

  /** See routeIsFullBleed; drops <main>'s phone side gutter. */
  protected readonly fullBleed = signal(false);

  deviceDetector: DeviceDetection;
  menuHeight: number = 0;
  providers: Provider[] = [];
  private touchStartX: number = 0;
  private touchStartY: number = 0;
  @ViewChild("scrollContainer", { read: ElementRef, static: false }) scrollContainer: ElementRef;
  @ViewChild("pageTransition", { static: false }) pageTransition: ElementRef<HTMLElement>;

  constructor() {
    this.deviceDetector = new DeviceDetector();
    this.dialogFactory = new DialogFactory();

    // Set now too, for a layout created after its first navigation. NavigationEnd comes in the
    // same synchronous pass as the new page's activation, before it's rendered.
    const update = () =>
      this.fullBleed.set(routeIsFullBleed(this.router.routerState.snapshot.root));
    update();
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(update);
  }

  /** True while the user has no saved location and must be prompted before using the app. */
  protected showLocationOverlay = computed(() => {
    const location = this.userStore.preferences.preferenceView()?.userLocation;
    return this.appState.state().isStoreInitialised && location === undefined;
  });

  async ngOnInit() {
    await this.syncService.fullSync(false);
    this.providers = await this.providerService.getAll();

    document.body.classList.remove("layout_frontend");
  }

  openHelpBox = async () => {
    this.dialogFactory.callHelpBox();
  };

  updateMenuHeight(height: number) {
    this.menuHeight = height;
  }

  ngAfterViewInit(): void {
    const el: HTMLElement | undefined = this.scrollContainer?.nativeElement;
    if (!el) {
      return;
    }
    // Registered by hand instead of through (wheel)/(touchstart)/(touchmove) template bindings:
    // those run inside the Angular zone, so every touchmove (60-120 a second while scrolling) ran
    // a full change-detection pass. These handlers only read coordinates and set a signal, so
    // they run outside the zone (a signal update schedules its own pass) and are passive.
    this.ngZone.runOutsideAngular(() => {
      el.addEventListener("wheel", this.onWheel, { passive: true });
      el.addEventListener("touchstart", this.onTouchStart, { passive: true });
      el.addEventListener("touchend", this.onTouchStart, { passive: true });
      el.addEventListener("touchmove", this.onTouchMove, { passive: true });
    });
  }

  ngOnDestroy(): void {
    const el: HTMLElement | undefined = this.scrollContainer?.nativeElement;
    el?.removeEventListener("wheel", this.onWheel);
    el?.removeEventListener("touchstart", this.onTouchStart);
    el?.removeEventListener("touchend", this.onTouchStart);
    el?.removeEventListener("touchmove", this.onTouchMove);
  }

  /**
   * Detects scroll direction based on wheel/touch input.
   * Uses the wheel deltaY to determine direction, which is more reliable
   * than tracking scroll position (which can be affected by content height changes).
   */
  onWheel = (event: WheelEvent): void => {
    // Zoom gestures and mostly-horizontal swipes aren't page scrolls (also skips deltaY === 0).
    if (event.ctrlKey || Math.abs(event.deltaX) >= Math.abs(event.deltaY)) {
      return;
    }
    this.scrollDirectionService.report(
      event.deltaY > 0 ? "down" : "up",
      this.scrollContainer?.nativeElement,
    );
  };

  /**
   * Handle touch start for mobile devices. Also runs on touchend: when the first finger of a pinch
   * lifts, no touchstart fires, so the next move is measured from the finger still down.
   */
  onTouchStart = (event: TouchEvent): void => {
    const touch = event.touches[0];
    if (touch) {
      this.touchStartX = touch.clientX;
      this.touchStartY = touch.clientY;
    }
  };

  /**
   * Handle touch move for mobile devices - detect scroll direction.
   * Each sample's distance is passed along so a stray pixel against the gesture can't flip it.
   */
  onTouchMove = (event: TouchEvent): void => {
    const touch = event.touches[0];
    const deltaX = this.touchStartX - touch.clientX;
    const deltaY = this.touchStartY - touch.clientY;
    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;

    // Pinches and mostly-sideways swipes aren't page scrolls (also skips deltaY === 0).
    if (event.touches.length > 1 || Math.abs(deltaX) >= Math.abs(deltaY)) {
      return;
    }

    // deltaY > 0: finger moving up, page scrolling down
    this.scrollDirectionService.report(
      deltaY > 0 ? "down" : "up",
      this.scrollContainer?.nativeElement,
      Math.abs(deltaY),
    );
  };

  onRouterOutletActivate() {
    // Instant, not smooth: a smooth-scroll animation races the incoming page's layout — the new
    // component mounts and reflows on the same tick, clamping the scroll range mid-animation and
    // leaving the page partway down. An instant reset lands at the top in one synchronous frame.
    this.scrollContainer?.nativeElement?.scrollTo({ top: 0, left: 0, behavior: "auto" });
    this.playPageEnterAnimation();
  }

  /**
   * Animates the wrapper around the outlet rather than the page host: with BrowserAnimationsModule
   * the outgoing host is still in the DOM (removal is deferred) when `activate` fires, and page hosts
   * may be `display: inline`, which ignores `transform`.
   */
  private playPageEnterAnimation() {
    playPageEnterAnimation([this.pageTransition?.nativeElement]);
  }

  /** Disables pull-to-refresh while loading, wizard, or sync already running. */
  protected pullToRefreshDisabled(): boolean {
    return (
      // this.showWizard() ||
      this.appState.state().status === "pending" || this.syncService.syncInProgress
    );
  }
}
